// Conflits de synchronisation (R9) : pour chaque rapport reçu du terrain sur une mission qui avait
// changé, la version de l'appareil (rapport, version attendue, saisie) et la version serveur (mission
// à la réception et aujourd'hui) côte à côte ; le responsable technique retient la version de
// l'appareil ou l'écarte ; les conflits tranchés restent lisibles.
import { Bandeau, Bouton, DialogueConfirmation, EtatVide, Filtres, Lien, LienBouton, PuceEtat, ZoneTexte, useFiltresPersistes } from '@cc/ui';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { INVALIDER_INTERVENTIONS, trancherConflit, useConflits } from './api';
import { duree, libelleEtatMission, libelleTypeRapport } from './logique';
import { Photos } from './Photo';
import type { Conflit } from './types';
import commun from '../../commun/commun.module.css';
import styles from './interventions.module.css';

function Decision({ c, decision, onFermer }: { c: Conflit; decision: 'retenir' | 'ecarter'; onFermer: () => void }) {
  const [motif, setMotif] = useState('');
  const action = useAction({
    executer: () => trancherConflit(c.id, decision, motif.trim() || undefined),
    succes: decision === 'retenir' ? 'Version de l\'appareil retenue : le rapport est pris en compte, la mission avance.' : 'Version de l\'appareil écartée : la mission reprend son cours, le rapport reste lisible.',
    invalider: INVALIDER_INTERVENTIONS,
    apres: onFermer,
  });
  return (
    <DialogueConfirmation
      ouvert
      titre={decision === 'retenir' ? 'Retenir la version de l\'appareil' : 'Écarter la version de l\'appareil'}
      message={<p>{decision === 'retenir' ? 'Le rapport saisi sur le terrain devient valable sur la mission telle qu\'elle est aujourd\'hui.' : 'Le rapport est écarté mais conservé ; rien n\'est effacé.'} La version serveur de la mission augmente.</p>}
      libelleConfirmer={decision === 'retenir' ? 'Retenir' : 'Écarter'}
      variante={decision === 'ecarter' ? 'danger' : undefined}
      enCours={action.enCours}
      onConfirmer={() => action.lancer(undefined)}
      onAnnuler={onFermer}
    >
      <ZoneTexte libelle="Motif" value={motif} onChange={(e) => setMotif(e.target.value)} facultatif rows={3} />
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </DialogueConfirmation>
  );
}

function Instantane({ v }: { v: Record<string, unknown> | null }) {
  if (!v) return <p>Non disponible.</p>;
  const agent = v.agent as { nom?: string | null } | null | undefined;
  const equipe = v.equipe as { nom?: string | null } | null | undefined;
  return (
    <dl className={commun.definitions}>
      <dt>Version</dt>
      <dd>{String(v.version ?? '')}</dd>
      <dt>État</dt>
      <dd>{libelleEtatMission(String(v.etat ?? ''))}</dd>
      <dt>Équipe</dt>
      <dd>{equipe?.nom ?? 'non renseignée'}</dd>
      <dt>Agent</dt>
      <dd>{agent?.nom ?? 'toute l\'équipe'}</dd>
      <dt>Date</dt>
      <dd>{v.date ? date(String(v.date)) : 'non renseignée'}</dd>
      <dt>Consignes</dt>
      <dd>{(v.consignes as string | null) ?? 'aucune'}</dd>
    </dl>
  );
}

function CarteConflit({ c }: { c: Conflit }) {
  const [decision, setDecision] = useState<'retenir' | 'ecarter' | null>(null);
  const r = c.appareil.rapport;
  return (
    <section className={commun.pile} aria-label={`Conflit sur ${c.serveur.intervention.numero}`}>
      <h2 className={styles.resume}>
        <Lien href={`/interventions/${c.serveur.intervention.id}`}>{c.serveur.intervention.numero}</Lien>
        <span>{c.serveur.intervention.titre}</span>
        <PuceEtat ton={c.etat === 'conflit' ? 'retard' : 'ok'} libelle={c.etat === 'conflit' ? 'À trancher' : c.etat === 'retenu' ? 'Version de l\'appareil retenue' : 'Version de l\'appareil écartée'} />
      </h2>
      <div className={styles.versions}>
        <article className={styles.version} aria-label="Version de l'appareil">
          <h3>Version de l&apos;appareil</h3>
          <dl className={commun.definitions}>
            <dt>Rapport</dt>
            <dd>{libelleTypeRapport(r.type)}, saisi le {dateHeure(c.appareil.enregistre_sur_appareil_le)}{c.appareil.auteur?.nom ? ` par ${c.appareil.auteur.nom}` : ''}</dd>
            <dt>Version attendue</dt>
            <dd>{c.appareil.version_attendue} (reçu le {dateHeure(c.recu_le)})</dd>
            <dt>Constats</dt>
            <dd>{r.contenu.constats || 'non renseignés'}</dd>
            <dt>Travaux</dt>
            <dd>{r.contenu.travaux || 'non renseignés'}</dd>
            <dt>Durée</dt>
            <dd>{duree(r.contenu.duree_minutes)}{r.contenu.termine ? ' · mission terminée' : ''}</dd>
          </dl>
          <Photos photos={r.photos} vide="Aucune photo." />
        </article>
        <article className={styles.version} aria-label="Version du serveur">
          <h3>Version du serveur</h3>
          <h4>À la réception du rapport</h4>
          <Instantane v={c.serveur.a_la_reception} />
          <h4>Aujourd&apos;hui</h4>
          <Instantane v={c.serveur.aujourdhui as unknown as Record<string, unknown>} />
        </article>
      </div>
      {c.tranche ? (
        <p>
          Tranché le {c.tranche.le ? dateHeure(c.tranche.le) : ''} : {c.tranche.decision === 'retenir' ? 'version de l\'appareil retenue' : 'version de l\'appareil écartée'}
          {c.tranche.motif ? ` (${c.tranche.motif})` : ''}.
        </p>
      ) : (
        <div className={styles.actionsFiche}>
          <Bouton variante="principal" icone={CheckCircle2} onClick={() => setDecision('retenir')}>
            Retenir la version de l&apos;appareil
          </Bouton>
          <Bouton variante="danger" onClick={() => setDecision('ecarter')}>
            Écarter la version de l&apos;appareil
          </Bouton>
        </div>
      )}
      {decision ? <Decision c={c} decision={decision} onFermer={() => setDecision(null)} /> : null}
    </section>
  );
}

export function PageConflits() {
  const [filtres, setFiltres] = useFiltresPersistes('conflits', { etat: 'en_attente' });
  const etat = filtres.etat === 'tous' ? 'tous' : 'en_attente';
  const conflits = useConflits(etat);
  return (
    <>
      <EnTetePage
        etiquette="Interventions"
        titre="Conflits de synchronisation"
        description="Un rapport saisi hors connexion sur une mission qui a changé entre-temps (réaffectation, annulation) est conservé ici avec la version serveur : rien n'est écrasé tant que le responsable technique n'a pas tranché."
        actions={
          <LienBouton href="/interventions" icone={ArrowLeft}>
            Interventions
          </LienBouton>
        }
      />
      <Filtres libelle="Filtre des conflits" groupes={[{ id: 'etat', libelle: 'Afficher', options: [{ valeur: 'en_attente', libelle: 'À trancher' }, { valeur: 'tous', libelle: 'Tous, tranchés compris' }] }]} valeurs={filtres} onChanger={setFiltres} />
      <EtatRequete chargement={conflits.isPending} erreur={conflits.error} onReessayer={() => void conflits.refetch()} lot="L3-A" fonction="La liste des conflits">
        {(conflits.data ?? []).length === 0 ? (
          <EtatVide titre="Aucun conflit" icone={CheckCircle2}>
            <p>Aucun rapport de terrain n&apos;attend de décision.</p>
          </EtatVide>
        ) : (
          <div className={commun.pile}>
            {(conflits.data ?? []).map((c) => (
              <CarteConflit key={c.id} c={c} />
            ))}
          </div>
        )}
      </EtatRequete>
    </>
  );
}
